import {
  getThreatReviewUrlReportDetail,
  getThreatReviewUrlReports,
  updateThreatReviewUrlReport,
  addThreatReviewUrlReport,
  uploadScamEvidence,
  approveRejectThreatReviewUrlReport,
} from "@/services";
import {
  AddThreatReviewUrlReportParams,
  ApproveRejectThreatReviewUrlReportParams,
  ThreatReviewUrlReportParams,
  UpdateThreatReviewUrlReportParams,
  UploadScamEvidenceParams,
} from "@/types";
import { useMutation, useQuery } from "@tanstack/react-query";

const THREAT_REVIEW_URL_REPORTS_QUERY_KEY = ["threat-review-url-reports"] as const;
const THREAT_REVIEW_WAITING_APPROVAL_URL_REPORTS_QUERY_KEY = [
  "threat-review-waiting-approval-url-reports",
] as const;
const THREAT_REVIEW_URL_REPORT_DETAIL_QUERY_KEY = ["threat-review-url-report-detail"] as const;

export function useThreatReviewUrlReports({
  offset = 0,
  limit = 10,
  status,
  search,
  source,
  priority,
  approvalStatus = "all",
}: ThreatReviewUrlReportParams) {
  return useQuery({
    queryKey: [
      THREAT_REVIEW_URL_REPORTS_QUERY_KEY,
      offset,
      limit,
      status,
      search,
      source,
      priority,
    ],
    queryFn: async () => {
      const response = await getThreatReviewUrlReports({
        offset,
        limit,
        status,
        search,
        source,
        priority,
        approvalStatus,
      });
      return response ?? null;
    },
    staleTime: 0,
    gcTime: 0,
  });
}

export function useThreatReviewWaitingApprovalUrlReports({
  offset = 0,
  limit = 10,
  status = "all",
  search,
  source,
  priority = "all",
  approvalStatus = "all",
  isForApproval,
  allReport,
}: ThreatReviewUrlReportParams) {
  return useQuery({
    queryKey: [
      THREAT_REVIEW_WAITING_APPROVAL_URL_REPORTS_QUERY_KEY,
      offset,
      limit,
      status,
      search,
      source,
      priority,
      approvalStatus,
      isForApproval,
      allReport,
    ],
    queryFn: async () => {
      const response = await getThreatReviewUrlReports({
        offset,
        limit,
        status,
        search,
        source,
        priority,
        approvalStatus,
        isForApproval,
        allReport,
      });
      return response ?? null;
    },
    staleTime: 0,
    gcTime: 0,
  });
}

export function useThreatReviewUrlReportDetail({ reportId }: { reportId: string }) {
  return useQuery({
    queryKey: [THREAT_REVIEW_URL_REPORT_DETAIL_QUERY_KEY, reportId],
    queryFn: async () => {
      const response = await getThreatReviewUrlReportDetail({ reportId });
      return response ?? null;
    },
    staleTime: 0,
    gcTime: 0,
    enabled: !!reportId,
  });
}

export function useUpdateThreatReviewUrlReport() {
  return useMutation({
    mutationFn: async ({
      reportId,
      reviewStatus,
      category,
      adminNotes,
      summary,
      keyAnalysisFactors,
      finalizedScore,
      email,
      is_submit_for_approval = false,
    }: UpdateThreatReviewUrlReportParams) =>
      updateThreatReviewUrlReport({
        reportId,
        reviewStatus,
        category,
        adminNotes,
        summary,
        keyAnalysisFactors,
        finalizedScore,
        email,
        is_submit_for_approval,
      }),
  });
}

export function useApproveRejectThreatReviewUrlReport() {
  return useMutation({
    mutationFn: async ({
      email,
      finalDecision,
      isApprovalAction,
      reportId,
    }: ApproveRejectThreatReviewUrlReportParams) =>
      approveRejectThreatReviewUrlReport({
        email,
        finalDecision,
        isApprovalAction,
        reportId,
      }),
  });
}

export function useAddThreatReviewUrlReport() {
  return useMutation({
    mutationFn: async ({
      email,
      evidence,
      url,
      type,
      description,
      isByAdmin,
    }: AddThreatReviewUrlReportParams) =>
      addThreatReviewUrlReport({ url, type, description, email, evidence, isByAdmin }),
  });
}

export function useUploadScamEvidence() {
  const { mutateAsync: mutationUploadScamEvidence, isPending: isUploadScamEvidenceLoading } =
    useMutation({
      mutationFn: ({ fileData, filename }: UploadScamEvidenceParams) =>
        uploadScamEvidence({ fileData, filename }),
    });
  return {
    mutationUploadScamEvidence,
    isUploadScamEvidenceLoading,
  };
}
